import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Convention : les fonctions retournent { ok, code?, raw?, ...data }.
 * `code` est une clé i18n (`errors.xxx`) que le composant traduit via useT().
 */

// ---------------- Waitlist ----------------
export async function joinWaitlist({ email, source, lang }) {
  try {
    const res = await api.post('/waitlist', { email, source, lang });
    return { ok: true, raw: res.data?.message };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 409) return { ok: false, alreadyIn: true, code: 'email_duplicate', raw: detail };
    if (status === 429) return { ok: false, code: 'rate_limit_generic', raw: detail };
    if (status === 400) return { ok: false, code: 'email_invalid', raw: detail };
    if (status === 422) return { ok: false, code: 'email_invalid', raw: detail };
    return { ok: false, code: 'generic', raw: detail };
  }
}

export async function fetchWaitlistCount() {
  try {
    const res = await api.get('/waitlist/count');
    return typeof res.data?.total === 'number' ? res.data.total : null;
  } catch {
    return null;
  }
}

// ---------------- Contact ----------------
export async function sendContact({ name, email, subject, message }) {
  try {
    const res = await api.post('/contact', { name, email, subject, message });
    return { ok: true, raw: res.data?.message };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 429) return { ok: false, code: 'rate_limit_generic', raw: detail };
    if (status === 400) return { ok: false, code: 'contact_invalid_fields', raw: detail };
    if (status === 422) return { ok: false, code: 'contact_invalid_fields', raw: detail };
    return { ok: false, code: 'contact_generic', raw: detail };
  }
}

// ---------------- Showcases (public) ----------------
export async function fetchShowcases({ vertical, limit = 3, lang } = {}) {
  try {
    const params = { limit };
    if (vertical) params.vertical = vertical;
    if (lang) params.lang = lang;
    const res = await api.get('/showcases', { params });
    return res.data?.items || [];
  } catch {
    return [];
  }
}

// ---------------- Generator ----------------
export async function generateConcept({ vertical, description, lang = 'fr' }) {
  try {
    const res = await api.post('/generator/concept', { vertical, description, lang });
    return { ok: true, teaser: res.data };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 429) return { ok: false, status, code: 'rate_limit_generator', raw: detail };
    if (status === 400) return { ok: false, status, code: 'generate_generic', raw: detail };
    if (status === 503) return { ok: false, status, code: 'llm_unavailable', raw: detail };
    return { ok: false, status: status || 0, code: 'generate_generic', raw: detail };
  }
}

export async function unlockConcept(conceptId, email) {
  try {
    const res = await api.post(`/generator/concept/${conceptId}/unlock`, { email });
    return { ok: true, concept: res.data.concept, alreadyIn: !!res.data.already_in_waitlist };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 400) return { ok: false, status, code: 'email_invalid', raw: detail };
    if (status === 404) return { ok: false, status, code: 'concept_not_found', raw: detail };
    if (status === 429) return { ok: false, status, code: 'unlock_rate_limit', raw: detail };
    return { ok: false, status: status || 0, code: 'unlock_generic', raw: detail };
  }
}

// ---------------- Admin (FR only) ----------------
const ADMIN_TOKEN_KEY = 'om_admin_token';
const ADMIN_EXPIRES_KEY = 'om_admin_expires';

export function saveAdminSession({ token, expires_at }) {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  if (expires_at) localStorage.setItem(ADMIN_EXPIRES_KEY, expires_at);
}
export function clearAdminSession() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_EXPIRES_KEY);
}
export function getAdminToken() {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  const exp = localStorage.getItem(ADMIN_EXPIRES_KEY);
  if (!t) return null;
  if (exp) {
    const dt = new Date(exp);
    if (!Number.isNaN(dt.getTime()) && dt.getTime() < Date.now()) {
      clearAdminSession();
      return null;
    }
  }
  return t;
}

function adminHeaders() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function adminLogin({ username, password }) {
  try {
    const res = await api.post('/admin/login', { username, password });
    saveAdminSession(res.data);
    return { ok: true };
  } catch (err) {
    const detail = err?.response?.data?.detail;
    return { ok: false, message: typeof detail === 'string' ? detail : 'Identifiants incorrects.' };
  }
}

export async function adminMe() {
  try {
    const res = await api.get('/admin/me', { headers: adminHeaders() });
    return res.data;
  } catch {
    return null;
  }
}

export async function adminStats() {
  const res = await api.get('/admin/stats', { headers: adminHeaders() });
  return res.data;
}

export async function adminWaitlist({ page = 1, limit = 50 } = {}) {
  const res = await api.get('/admin/waitlist', { headers: adminHeaders(), params: { page, limit } });
  return res.data;
}

export async function adminContact({ page = 1, limit = 50 } = {}) {
  const res = await api.get('/admin/contact', { headers: adminHeaders(), params: { page, limit } });
  return res.data;
}

export async function adminConcepts({ page = 1, limit = 20, vertical, showcase } = {}) {
  const params = { page, limit };
  if (vertical) params.vertical = vertical;
  if (typeof showcase === 'boolean') params.showcase = showcase;
  const res = await api.get('/admin/concepts', { headers: adminHeaders(), params });
  return res.data;
}

export async function adminToggleShowcase(conceptId, isShowcase) {
  const res = await api.patch(
    `/admin/concepts/${conceptId}/showcase`,
    { is_showcase: isShowcase },
    { headers: adminHeaders() },
  );
  return res.data;
}

export function adminExportUrl(kind) {
  return `${BACKEND_URL}/api/admin/${kind}/export`;
}

// ---------------- Admin analytics (Phase 5) ----------------
export async function adminAnalyticsFunnel({ period = 30, lang } = {}) {
  const params = { period };
  if (lang) params.lang = lang;
  const res = await api.get('/admin/analytics/funnel', { headers: adminHeaders(), params });
  return res.data;
}

export async function adminAnalyticsVerticals({ period = 30, lang } = {}) {
  const params = { period };
  if (lang) params.lang = lang;
  const res = await api.get('/admin/analytics/verticals', { headers: adminHeaders(), params });
  return res.data;
}

export async function adminAnalyticsWaitlistDaily({ days = 7 } = {}) {
  const res = await api.get('/admin/analytics/waitlist_daily', {
    headers: adminHeaders(),
    params: { days },
  });
  return res.data;
}

export async function adminAnalyticsOverview({ period = 30, lang } = {}) {
  const params = { period };
  if (lang) params.lang = lang;
  const res = await api.get('/admin/analytics/overview', { headers: adminHeaders(), params });
  return res.data;
}

// ---------------- Admin payments (Phase 6) ----------------
export async function adminPayments({ page = 1, limit = 20 } = {}) {
  const res = await api.get('/admin/payments', {
    headers: adminHeaders(),
    params: { page, limit },
  });
  return res.data;
}

// ---------------- Admin creators (Phase 8 + Phase 12) ----------------
export async function adminListCreators({ page = 1, limit = 20, q } = {}) {
  const params = { page, limit };
  if (q) params.q = q;
  const res = await api.get('/admin/creators', { headers: adminHeaders(), params });
  return res.data;
}

export async function adminGrantPlan(userId, { plan, interval }) {
  const res = await api.post(
    `/admin/creators/${encodeURIComponent(userId)}/grant`,
    { plan, interval },
    { headers: adminHeaders() },
  );
  return res.data;
}

export async function adminCreatorDetail(userId) {
  const res = await api.get(
    `/admin/creators/${encodeURIComponent(userId)}`,
    { headers: adminHeaders() },
  );
  return res.data;
}

export async function adminResetCreatorPassword(userId) {
  const res = await api.post(
    `/admin/creators/${encodeURIComponent(userId)}/reset-password`,
    {},
    { headers: adminHeaders() },
  );
  return res.data;
}

export async function adminDeleteCreator(userId, { email_confirmation }) {
  const res = await api.delete(
    `/admin/creators/${encodeURIComponent(userId)}`,
    {
      headers: adminHeaders(),
      data: { email_confirmation },
    },
  );
  return res.data;
}

// ---------------- Checkout (Phase 6, étendu Phase 8) ----------------
export async function createCheckoutSession({ plan, interval, lang, user_id, email }) {
  try {
    const body = { plan, interval, lang };
    if (user_id) body.user_id = user_id;
    if (email) body.email = email;
    const res = await api.post('/checkout/session', body);
    return { ok: true, ...res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) return { ok: false, code: 'rate_limit_generic' };
    if (status === 400) return { ok: false, code: 'checkout_invalid' };
    return { ok: false, code: 'checkout_generic' };
  }
}

export async function getCheckoutStatus(sessionId) {
  try {
    const res = await api.get(`/checkout/status/${encodeURIComponent(sessionId)}`);
    return { ok: true, ...res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return { ok: false, code: 'not_found' };
    return { ok: false, code: 'checkout_generic' };
  }
}

// ---------------- Prototype (Phase 7) ----------------
export async function generatePrototype(conceptId) {
  try {
    const res = await api.post('/prototype/generate', { concept_id: conceptId });
    return { ok: true, prototype: res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 403) return { ok: false, code: 'prototype_locked' };
    if (status === 404) return { ok: false, code: 'prototype_not_found' };
    if (status === 429) return { ok: false, code: 'prototype_rate_limit' };
    if (status === 503) return { ok: false, code: 'prototype_unavailable' };
    return { ok: false, code: 'prototype_generic' };
  }
}

export async function getPrototype(conceptId) {
  try {
    const res = await api.get(`/prototype/${encodeURIComponent(conceptId)}`);
    return { ok: true, prototype: res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 403) return { ok: false, code: 'prototype_locked' };
    if (status === 404) return { ok: false, code: 'prototype_not_found' };
    return { ok: false, code: 'prototype_generic' };
  }
}

export async function adminDownloadExport(kind) {
  const res = await api.get(`/admin/${kind}/export`, {
    headers: adminHeaders(),
    responseType: 'blob',
  });
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename = `openmeta-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
